import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import {
  fetchContracts,
  deleteContract,
  duplicateContract,
  setQuery,
  toggleContractSelection,
  selectAllContracts,
  clearContractSelection,
  setShowCreateModal,
  bulkUpdateStatus,
  clearError,
} from '../../store/slices/contractsSlice';
import type { RootState, AppDispatch } from '../../store';
import type { Contract, ContractStatus } from '../../types/contracts';
import {
  CONTRACT_STATUSES,
  CONTRACT_SORT_OPTIONS,
  CONTRACT_STATUS_COLORS,
} from '../../types/contracts';
import contractService from '../../services/contractService';

const ContractsList: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();

  const {
    contracts,
    loading,
    error,
    currentPage,
    totalPages,
    totalContracts,
    query,
    selectedContractIds,
    bulkOperationLoading,
  } = useSelector((state: RootState) => state.contracts);

  const [showBulkActions, setShowBulkActions] = useState(false);
  const [searchDebounce, setSearchDebounce] = useState<NodeJS.Timeout>();

  // Load contracts on component mount and when filters change
  useEffect(() => {
    if (projectId) {
      dispatch(fetchContracts({ projectId, params: query }));
    }
  }, [dispatch, projectId, query]);

  // Clear error when component unmounts
  useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  // Handle search with debouncing
  const handleSearch = (searchTerm: string) => {
    if (searchDebounce) {
      clearTimeout(searchDebounce);
    }

    const timeout = setTimeout(() => {
      dispatch(setQuery({ search: searchTerm, page: 1 }));
    }, 300);

    setSearchDebounce(timeout);
  };

  // Handle filter changes
  const handleFilterChange = (key: string, value: any) => {
    dispatch(setQuery({ [key]: value, page: 1 }));
  };

  // Handle pagination
  const handlePageChange = (page: number) => {
    dispatch(setQuery({ page }));
  };

  // Handle contract actions
  const handleViewContract = (contractId: string) => {
    navigate(`/projects/${projectId}/contracts/${contractId}`);
  };

  const handleEditContract = (contractId: string) => {
    navigate(`/projects/${projectId}/contracts/${contractId}/edit`);
  };

  const handleDeleteContract = async (contractId: string) => {
    if (window.confirm('Are you sure you want to cancel this contract?')) {
      try {
        await dispatch(deleteContract(contractId)).unwrap();
      } catch (error) {
        console.error('Failed to delete contract:', error);
      }
    }
  };

  const handleDuplicateContract = async (contractId: string) => {
    try {
      await dispatch(duplicateContract(contractId)).unwrap();
    } catch (error) {
      console.error('Failed to duplicate contract:', error);
    }
  };

  // Handle bulk operations
  const handleBulkStatusUpdate = async (status: ContractStatus) => {
    if (selectedContractIds.length === 0) return;

    try {
      await dispatch(bulkUpdateStatus({ contractIds: selectedContractIds, status })).unwrap();
      setShowBulkActions(false);
    } catch (error) {
      console.error('Failed to update contract status:', error);
    }
  };

  // Memoized computed values
  const selectedContractsCount = selectedContractIds.length;
  const allSelected = contracts.length > 0 && selectedContractIds.length === contracts.length;
  const someSelected =
    selectedContractIds.length > 0 && selectedContractIds.length < contracts.length;

  const statusCounts = useMemo(() => {
    return contracts.reduce(
      (acc, contract) => {
        acc[contract.status] = (acc[contract.status] || 0) + 1;
        return acc;
      },
      {} as Record<ContractStatus, number>
    );
  }, [contracts]);

  if (loading && contracts.length === 0) {
    return (
      <div className="max-w-7xl mx-auto py-6 px-2">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded mb-4"></div>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-6 px-2">
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Contracts</h1>
            <p className="text-sm text-gray-600 mt-1">{totalContracts} total contracts</p>
          </div>
          <button
            onClick={() => dispatch(setShowCreateModal(true))}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-brand-orange hover:bg-brand-orange-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-orange"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            New Contract
          </button>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
          {CONTRACT_STATUSES.slice(1).map(({ value, label }) => {
            const count = statusCounts[value as ContractStatus] || 0;
            const color = CONTRACT_STATUS_COLORS[value as ContractStatus];
            return (
              <div
                key={value}
                className={`p-3 rounded-lg border cursor-pointer hover:shadow-md transition-shadow ${
                  query.status === value ? `border-${color}-500 bg-${color}-50` : 'border-gray-200'
                }`}
                onClick={() => handleFilterChange('status', value)}
              >
                <div className={`text-lg font-semibold text-${color}-600`}>{count}</div>
                <div className="text-xs text-gray-600">{label}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search contracts</label>
            <input
              type="text"
              placeholder="Contract title, number..."
              defaultValue={query.search || ''}
              onChange={e => handleSearch(e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-orange focus:border-brand-orange sm:text-sm"
            />
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={query.status || 'all'}
              onChange={e => handleFilterChange('status', e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-orange focus:border-brand-orange sm:text-sm"
            >
              {CONTRACT_STATUSES.map(({ value, label }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          {/* Sort By */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sort by</label>
            <select
              value={query.sortBy || 'createdAt'}
              onChange={e => handleFilterChange('sortBy', e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-orange focus:border-brand-orange sm:text-sm"
            >
              {CONTRACT_SORT_OPTIONS.map(({ value, label }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          {/* Sort Order */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Order</label>
            <select
              value={query.sortOrder || 'desc'}
              onChange={e => handleFilterChange('sortOrder', e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-orange focus:border-brand-orange sm:text-sm"
            >
              <option value="desc">Newest first</option>
              <option value="asc">Oldest first</option>
            </select>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex">
            <svg
              className="w-5 h-5 text-red-400 mt-0.5 mr-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div>
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
            <button
              onClick={() => dispatch(clearError())}
              className="ml-auto text-red-400 hover:text-red-600"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Bulk Actions */}
      {selectedContractsCount > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <span className="text-sm text-blue-700">
                {selectedContractsCount} contract{selectedContractsCount !== 1 ? 's' : ''} selected
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowBulkActions(!showBulkActions)}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Actions
              </button>
              <button
                onClick={() => dispatch(clearContractSelection())}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Clear selection
              </button>
            </div>
          </div>

          {showBulkActions && (
            <div className="mt-3 pt-3 border-t border-blue-200">
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => handleBulkStatusUpdate('review')}
                  disabled={bulkOperationLoading}
                  className="px-3 py-1 bg-yellow-100 text-yellow-800 text-sm rounded-md hover:bg-yellow-200 disabled:opacity-50"
                >
                  Mark as Review
                </button>
                <button
                  onClick={() => handleBulkStatusUpdate('approved')}
                  disabled={bulkOperationLoading}
                  className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-md hover:bg-blue-200 disabled:opacity-50"
                >
                  Mark as Approved
                </button>
                <button
                  onClick={() => handleBulkStatusUpdate('cancelled')}
                  disabled={bulkOperationLoading}
                  className="px-3 py-1 bg-gray-100 text-gray-800 text-sm rounded-md hover:bg-gray-200 disabled:opacity-50"
                >
                  Cancel Contracts
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Contracts Table */}
      <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
        {contracts.length === 0 ? (
          <div className="p-12 text-center">
            <svg
              className="w-16 h-16 text-gray-400 mx-auto mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No contracts found</h3>
            <p className="text-gray-500 mb-4">
              {query.search || query.status !== 'all'
                ? 'Try adjusting your filters'
                : 'Get started by creating your first contract'}
            </p>
            {!query.search && query.status === 'all' && (
              <button
                onClick={() => dispatch(setShowCreateModal(true))}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-brand-orange hover:bg-brand-orange-dark"
              >
                Create Contract
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        ref={input => {
                          if (input) input.indeterminate = someSelected;
                        }}
                        onChange={e => {
                          if (e.target.checked) {
                            dispatch(selectAllContracts());
                          } else {
                            dispatch(clearContractSelection());
                          }
                        }}
                        className="rounded border-gray-300 text-brand-orange focus:ring-brand-orange"
                      />
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contract
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Party
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Dates
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Progress
                    </th>
                    <th className="relative px-6 py-3">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {contracts.map(contract => (
                    <ContractRow
                      key={contract.id}
                      contract={contract}
                      isSelected={selectedContractIds.includes(contract.id)}
                      onToggleSelect={() => dispatch(toggleContractSelection(contract.id))}
                      onView={() => handleViewContract(contract.id)}
                      onEdit={() => handleEditContract(contract.id)}
                      onDelete={() => handleDeleteContract(contract.id)}
                      onDuplicate={() => handleDuplicateContract(contract.id)}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Showing {(currentPage - 1) * (query.limit || 10) + 1} to{' '}
                    {Math.min(currentPage * (query.limit || 10), totalContracts)} of{' '}
                    {totalContracts} results
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Previous
                    </button>
                    {[...Array(Math.min(5, totalPages))].map((_, i) => {
                      const page = currentPage <= 3 ? i + 1 : currentPage - 2 + i;
                      if (page > totalPages) return null;
                      return (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`px-3 py-1 border rounded-md text-sm ${
                            page === currentPage
                              ? 'border-brand-orange bg-brand-orange text-white'
                              : 'border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      );
                    })}
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Contract Row Component
interface ContractRowProps {
  contract: Contract;
  isSelected: boolean;
  onToggleSelect: () => void;
  onView: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
}

const ContractRow: React.FC<ContractRowProps> = ({
  contract,
  isSelected,
  onToggleSelect,
  onView,
  onEdit,
  onDelete,
  onDuplicate,
}) => {
  const [showActions, setShowActions] = useState(false);
  const statusColor = CONTRACT_STATUS_COLORS[contract.status];
  const isExpired = contractService.isContractExpired(contract);
  const daysUntilExpiration = contractService.getDaysUntilExpiration(contract);
  const progress = contractService.calculateProgress(contract.milestones);

  return (
    <tr className={`hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''}`}>
      <td className="px-6 py-4">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onToggleSelect}
          className="rounded border-gray-300 text-brand-orange focus:ring-brand-orange"
        />
      </td>
      <td className="px-6 py-4">
        <div>
          <div className="flex items-center">
            <button
              onClick={onView}
              className="text-sm font-medium text-gray-900 hover:text-brand-orange"
            >
              {contract.title}
            </button>
            {isExpired && (
              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                Expired
              </span>
            )}
          </div>
          <div className="text-sm text-gray-500">{contract.contractNumber}</div>
        </div>
      </td>
      <td className="px-6 py-4">
        <div className="text-sm text-gray-900">{contract.party?.name || 'Unknown Party'}</div>
        <div className="text-sm text-gray-500">{contract.party?.contactPerson}</div>
      </td>
      <td className="px-6 py-4">
        <div className="text-sm font-medium text-gray-900">
          {contractService.formatCurrency(contract.amount, contract.currency)}
        </div>
      </td>
      <td className="px-6 py-4">
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${statusColor}-100 text-${statusColor}-800`}
        >
          {contractService.getStatusLabel(contract.status)}
        </span>
      </td>
      <td className="px-6 py-4 text-sm text-gray-500">
        <div>Start: {new Date(contract.startDate).toLocaleDateString()}</div>
        <div>End: {new Date(contract.endDate).toLocaleDateString()}</div>
        {daysUntilExpiration !== null && (
          <div
            className={
              daysUntilExpiration < 0
                ? 'text-red-600'
                : daysUntilExpiration <= 30
                  ? 'text-yellow-600'
                  : ''
            }
          >
            {daysUntilExpiration < 0
              ? `${Math.abs(daysUntilExpiration)} days overdue`
              : daysUntilExpiration === 0
                ? 'Expires today'
                : `${daysUntilExpiration} days remaining`}
          </div>
        )}
      </td>
      <td className="px-6 py-4">
        {contract.milestones.length > 0 && (
          <div>
            <div className="flex items-center">
              <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                <div
                  className={`bg-${progress === 100 ? 'green' : 'blue'}-600 h-2 rounded-full`}
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
              <span className="text-sm text-gray-600">{progress}%</span>
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {contract.milestones.filter(m => m.status === 'completed').length} of{' '}
              {contract.milestones.length} milestones
            </div>
          </div>
        )}
      </td>
      <td className="px-6 py-4 text-right text-sm font-medium">
        <div className="relative">
          <button
            onClick={() => setShowActions(!showActions)}
            className="text-gray-400 hover:text-gray-500 focus:outline-none focus:text-gray-500"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
            </svg>
          </button>

          {showActions && (
            <div className="absolute right-0 z-10 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5">
              <div className="py-1" onMouseLeave={() => setShowActions(false)}>
                <button
                  onClick={onView}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  View Details
                </button>
                <button
                  onClick={onEdit}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  Edit Contract
                </button>
                <button
                  onClick={onDuplicate}
                  className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  Duplicate
                </button>
                <button
                  onClick={onDelete}
                  className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                >
                  Cancel Contract
                </button>
              </div>
            </div>
          )}
        </div>
      </td>
    </tr>
  );
};

export default ContractsList;
